import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Trash2, AlertTriangle, RefreshCw, Database } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLLECTION_LABELS = {
  patients: 'Patients',
  consultations: 'Consultations',
  prescriptions: 'Prescriptions',
  bills: 'Bills',
  medicines: 'Medicines',
  procedures: 'Procedures',
  patient_procedures: 'Patient Procedures',
  purchase_records: 'Purchase Records',
  audit_logs: 'Audit Logs',
};

const Cleanup = () => {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [onlyArchived, setOnlyArchived] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [result, setResult] = useState(null);

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/collection-counts`);
      setCounts(data);
    } catch (e) {
      console.error('Error fetching counts:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const openConfirm = (collection) => {
    setPendingDelete(collection);
    setConfirmText('');
    setOnlyArchived(false);
    setResult(null);
  };

  const handleBulkDelete = async () => {
    setDeleteLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/bulk-delete`, {
        collection: pendingDelete,
        confirmation: 'DELETE_PERMANENTLY',
        onlyArchived,
      });
      setResult(data);
      setPendingDelete(null);
      fetchCounts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const supportsArchivedFilter = pendingDelete === 'patients' || pendingDelete === 'consultations';
  const isPhraseCorrect = confirmText === 'DELETE_PERMANENTLY';

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="cleanup-title">
          Data Cleanup
        </h1>
        <p className="mt-2" style={{ color: '#6B7280' }}>Permanently delete records in bulk. Use with extreme caution.</p>
      </div>

      <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }}>
        <div className="flex items-start gap-3">
          <AlertTriangle size={24} style={{ color: '#991B1B', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="font-bold" style={{ color: '#991B1B' }}>Danger Zone</p>
            <p className="text-sm mt-1" style={{ color: '#991B1B' }}>
              Bulk deletion is <strong>permanent and irreversible</strong>. Every action is recorded in the Audit Trail.
              For Patients and Consultations, prefer archiving over deletion to preserve historical records.
            </p>
          </div>
        </div>
      </div>

      {result && (
        <div className="card p-4 mb-6" style={{ backgroundColor: '#D1FAE5' }} data-testid="bulk-delete-result">
          <p style={{ color: '#065F46' }}><strong>{result.message}</strong></p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12" style={{ color: '#6B7280' }}>Loading collection statistics...</div>
      ) : (
        <div className="card p-6">
          <table data-testid="cleanup-table">
            <thead>
              <tr>
                <th>Collection</th>
                <th>Total Records</th>
                <th>Archived</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(COLLECTION_LABELS).map(([key, label]) => {
                const c = counts[key] || { total: 0, archived: 0 };
                return (
                  <tr key={key} data-testid={`cleanup-row-${key}`}>
                    <td className="font-medium">
                      <div className="flex items-center gap-2" style={{ color: '#2D3A2A' }}>
                        <Database size={16} style={{ color: '#990099' }} />
                        {label}
                      </div>
                    </td>
                    <td className="text-2xl font-bold" style={{ color: '#2D3A2A' }}>{c.total}</td>
                    <td>{c.archived > 0 ? (
                      <span className="text-lg font-bold" style={{ color: '#92400E' }}>{c.archived}</span>
                    ) : <span style={{ color: '#6B7280' }}>—</span>}</td>
                    <td>
                      <button
                        onClick={() => openConfirm(key)}
                        disabled={c.total === 0}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                        data-testid={`bulk-delete-btn-${key}`}
                      >
                        <Trash2 size={16} />
                        Bulk Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 text-center">
        <button onClick={fetchCounts} className="secondary-btn flex items-center gap-2 mx-auto" data-testid="refresh-counts-btn">
          <RefreshCw size={16} />
          Refresh Counts
        </button>
      </div>

      {/* Confirm modal */}
      {pendingDelete && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
          data-testid="bulk-delete-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6" style={{ border: '1px solid #E5E0D8' }}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEE2E2' }}>
                <AlertTriangle size={24} style={{ color: '#EF4444' }} />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
                  Bulk Delete: {COLLECTION_LABELS[pendingDelete]}
                </h3>
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  You are about to permanently delete {onlyArchived ? `${counts[pendingDelete]?.archived || 0} archived` : counts[pendingDelete]?.total || 0} records from <strong>{pendingDelete}</strong>.
                  This action cannot be undone.
                </p>
              </div>
            </div>

            {supportsArchivedFilter && (counts[pendingDelete]?.archived || 0) > 0 && (
              <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#F5E6F5' }}>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={onlyArchived}
                    onChange={(e) => setOnlyArchived(e.target.checked)}
                    className="w-auto"
                    data-testid="only-archived-checkbox"
                  />
                  <span style={{ color: '#2D3A2A' }}>
                    Delete only <strong>archived</strong> records ({counts[pendingDelete]?.archived || 0} items) — recommended
                  </span>
                </label>
              </div>
            )}

            <div className="mb-4">
              <label>Type <code style={{ backgroundColor: '#F5E6F5', color: '#990099', padding: '2px 6px', borderRadius: '4px' }}>DELETE_PERMANENTLY</code> to confirm:</label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE_PERMANENTLY"
                data-testid="confirm-phrase-input"
              />
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => { setPendingDelete(null); setConfirmText(''); }}
                disabled={deleteLoading}
                className="secondary-btn px-5"
                data-testid="bulk-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={!isPhraseCorrect || deleteLoading}
                className="px-5 py-2 rounded-lg font-medium text-white transition-colors"
                style={{ backgroundColor: '#EF4444', opacity: (!isPhraseCorrect || deleteLoading) ? 0.4 : 1 }}
                data-testid="bulk-delete-confirm-btn"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cleanup;
